import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Table, Button, Modal, Form, Select, message, Space, Tag, Popconfirm, Alert, Spin, Row, Col, Empty, Input, Typography
} from 'antd';
import { SearchOutlined, LoadingOutlined, DownloadOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { S3_CONFIG } from '../../config/s3';
import {
  fetchApplications,
  updateApplication,
  deleteApplication,
  exportApplicationsToCsv,
  exportResumes
} from '../../store/features/dashboard/JobManagementSlice';
import { debounce } from '../../lib/utils';

const { Link } = Typography;

const JobsManagement = () => {
  const [modalVisible, setModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [currentApplicationId, setCurrentApplicationId] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [jobStatusFilter, setJobStatusFilter] = useState('');
  const [deletedFilter, setDeletedFilter] = useState(''); // New state for deleted filter
  const [isSearching, setIsSearching] = useState(false);
  const [isExportingResumes, setIsExportingResumes] = useState(false);
  const searchInputRef = useRef(null);
  const dispatch = useDispatch();

  const applicationState = useSelector((state) => state.jobApplications);
  const {
    applications,
    loading,
    error,
    total = 0,
    currentPage = 1,
    pageSize = 10,
  } = applicationState;

  const fetchData = useCallback((page, size, search = '', status = '', jobStatus = '', deleted = '') => {
    setIsSearching(true);
    dispatch(fetchApplications({
      page,
      limit: size || pageSize,
      search,
      status,
      jobStatus,
      deleted: deleted === '' ? undefined : deleted === 'Deleted' // Map to boolean or undefined
    })).finally(() => {
      setIsSearching(false);
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    });
  }, [dispatch, pageSize]);

  const debouncedSearch = useCallback(
    debounce((searchValue, statusValue, jobStatusValue, deletedValue) => {
      if (searchValue.length >= 2 || searchValue.length === 0) {
        fetchData(1, pageSize, searchValue, statusValue, jobStatusValue, deletedValue);
      }
    }, 500),
    [fetchData, pageSize]
  );

  const handleSearch = (value, event) => {
    setSearchText(value);
    if (event && event.type === 'change') {
      debouncedSearch(value, statusFilter, jobStatusFilter, deletedFilter);
    } else {
      if (value.length >= 2 || value.length === 0) {
        fetchData(1, pageSize, value, statusFilter, jobStatusFilter, deletedFilter);
      }
    }
  };

  const handleClearSearch = () => {
    setSearchText('');
    fetchData(1, pageSize, '', statusFilter, jobStatusFilter, deletedFilter);
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
    fetchData(1, pageSize, searchText, value, jobStatusFilter, deletedFilter);
  };

  const handleJobStatusFilterChange = (value) => {
    setJobStatusFilter(value);
    fetchData(1, pageSize, searchText, statusFilter, value, deletedFilter);
  };

  const handleDeletedFilterChange = (value) => {
    setDeletedFilter(value);
    fetchData(1, pageSize, searchText, statusFilter, jobStatusFilter, value);
  };

  useEffect(() => {
    fetchData(currentPage, pageSize, searchText, statusFilter, jobStatusFilter, deletedFilter);
  }, [currentPage, pageSize, fetchData]);

  const handlePageChange = (page, size) => {
    fetchData(page, size, searchText, statusFilter, jobStatusFilter, deletedFilter);
  };

  const resetModal = () => {
    setModalVisible(false);
    setCurrentApplicationId(null);
    form.resetFields();
  };

  const handleDelete = async (applicationId) => {
    try {
      await dispatch(deleteApplication(applicationId)).unwrap();
      message.success('Application deleted successfully!');
      if (applications.length === 1 && currentPage > 1) {
        fetchData(currentPage - 1, pageSize, searchText, statusFilter, jobStatusFilter, deletedFilter);
      } else {
        fetchData(currentPage, pageSize, searchText, statusFilter, jobStatusFilter, deletedFilter);
      }
    } catch (error) {
      message.error(error.message || 'Failed to delete application');
    }
  };

  const handleEdit = (application) => {
    setCurrentApplicationId(application.id);
    setModalVisible(true);
    form.setFieldsValue({
      status: application.status || 'Pending',
    });
  };

  const handleStatusUpdate = async (values) => {
    try {
      await dispatch(updateApplication({
        applicationId: currentApplicationId,
        applicationData: { status: values.status }
      })).unwrap();
      message.success('Application status updated successfully!');
      resetModal();
      fetchData(currentPage, pageSize, searchText, statusFilter, jobStatusFilter, deletedFilter);
    } catch (error) {
      console.error('Update error:', error);
      message.error(error.message || 'Failed to update application status');
    }
  };

  const handleSubmit = () => {
    form.validateFields()
      .then(values => {
        handleStatusUpdate(values);
      })
      .catch(error => {
        console.error('Validation error:', error);
        message.error('Please select a status');
      });
  };

  const handleExportCsv = async (params = {}) => {
    try {
      const { applicationId, jobId } = params;
      const exportParams = {
        jobId,
        applicationId,
        jobStatus: jobStatusFilter || undefined,
        jobTitle: searchText || undefined,
        status: statusFilter || undefined,
        deleted: deletedFilter === '' ? undefined : deletedFilter === 'Deleted' // Include deleted filter
      };
      const csvData = await dispatch(exportApplicationsToCsv(exportParams)).unwrap();
      const blob = new Blob([csvData], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const filename = applicationId
        ? `application_${applicationId}_${new Date().toISOString()}.csv`
        : `job_applications${jobId ? `_job_${jobId}` : ''}_${new Date().toISOString()}.csv`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      message.success('Applications exported to CSV successfully!');
    } catch (error) {
      message.error(error.message || 'Failed to export applications to CSV');
    }
  };

  const handleExportResumes = async () => {
    setIsExportingResumes(true);
    try {
      const params = {
        jobId: undefined,
        applicationId: undefined,
        jobStatus: jobStatusFilter || undefined,
        jobTitle: searchText || undefined,
        status: statusFilter || undefined,
        deleted: deletedFilter === '' ? undefined : deletedFilter === 'Deleted' // Include deleted filter
      };

      const response = await dispatch(exportResumes(params)).unwrap();

      // Create a download link for the ZIP file
      const blob = new Blob([response], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `resumes_${new Date().toISOString()}.zip`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      message.success('Resumes exported successfully!');
    } catch (error) {
      console.error('Export resumes error:', error);
      message.error(error.message || 'Failed to export resumes');
    } finally {
      setIsExportingResumes(false);
    }
  };

  const handleDownloadResume = async (resumePath) => {
    try {
      if (!resumePath || resumePath === 'resume_path') {
        throw new Error('Invalid resume path');
      }

      const response = await fetch(`${S3_CONFIG.publicUrl}/${resumePath}`, {
        method: 'GET',
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch resume: ${response.statusText}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const filename = resumePath.split('/').pop() || 'resume.pdf';

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download resume error:', error);
      message.error(error.message || 'Failed to download resume');
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 150,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: 200,
      render: email => <Link href={`mailto:${email}`}>{email}</Link>,
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      key: 'phone',
      width: 120,
      render: phone => phone || 'N/A',
    },
    {
      title: 'Resume',
      dataIndex: 'resume_path',
      key: 'resume',
      width: 100,
      render: (resume_path, record) =>
        resume_path && resume_path !== 'resume_path' && !record.deletedAt ? (
          <Button
            type="link"
            icon={<DownloadOutlined />}
            onClick={() => handleDownloadResume(resume_path)}
          >
            Download
          </Button>
        ) : (
          'N/A'
        ),
    },
    {
      title: 'Portfolio',
      dataIndex: 'portfolio_url',
      key: 'portfolio_url',
      width: 120,
      render: (portfolio_url, record) => portfolio_url && !record.deletedAt ? (
        <Link href={portfolio_url} target="_blank">View</Link>
      ) : 'N/A',
    },
    {
      title: 'Message',
      dataIndex: 'message',
      key: 'message',
      width: 150,
      render: message => message || 'N/A',
    },
    {
      title: 'Job Title',
      dataIndex: 'job_title',
      key: 'job_title',
      width: 150,
    },
    {
      title: 'Application Date',
      dataIndex: 'formattedcreatedat',
      key: 'application_date',
      width: 150,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status, record) => (
        <Tag
          color={
            record.deletedAt ? 'red' :
            status === 'Pending' ? 'blue' :
            status === 'Reviewed' ? 'cyan' :
            status === 'Shortlisted' ? 'green' :
            'red'
          }
          style={{ cursor: record.deletedAt ? 'not-allowed' : 'pointer' }}
          onClick={() => !record.deletedAt && handleEdit(record)}
        >
          {record.deletedAt ? 'Deleted' : status}
        </Tag>
      ),
    },
    {
      title: 'Deleted At',
      dataIndex: 'deletedAt',
      key: 'deletedAt',
      width: 120,
      render: deletedAt => deletedAt ? new Date(deletedAt).toLocaleDateString() : 'N/A',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      render: (_, record) => (
        <Space>
          <Button
            onClick={() => handleEdit(record)}
            disabled={record.deletedAt}
          >
            Edit Status
          </Button>
          <Button
            icon={<DownloadOutlined />}
            onClick={() => handleExportCsv({ applicationId: record.id })}
            disabled={record.deletedAt}
            title={`Export application ID ${record.id}`}
          >
            Export
          </Button>
          <Popconfirm
            title="Are you sure to delete this application?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
            disabled={record.deletedAt}
          >
            <Button danger disabled={record.deletedAt}>Delete</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (loading && applications.length === 0) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '24px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error Loading Applications"
        description={error}
        type="error"
        showIcon
        style={{ margin: '24px' }}
      />
    );
  }

  return (
    <div style={{
      padding: '24px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      width: '100%'
    }}>
      <h2 style={{ marginBottom: '16px' }}>Job Applications Management</h2>

      <div style={{
        width: '100%',
        maxWidth: '1200px',
        marginBottom: '16px'
      }}>
        <Row justify="space-between" align="middle" gutter={[16, 16]}>
          {/* Left side - Export Buttons */}
          <Col flex="none">
            <Space wrap>
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={handleExportResumes}
                loading={isExportingResumes}
              >
                Export Resumes
              </Button>
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={() => handleExportCsv()}
              >
                Export CSV
              </Button>
            </Space>
          </Col>

          {/* Right side - Filters and Search */}
          <Col flex="auto" style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Space wrap size={[8, 16]} style={{ justifyContent: 'flex-end', width: '100%' }}>
              <Select
                style={{ minWidth: 150, maxWidth: 200 }}
                placeholder="Job Status"
                allowClear
                onChange={handleJobStatusFilterChange}
                value={jobStatusFilter}
              >
                <Select.Option value="Active">Active</Select.Option>
                <Select.Option value="Inactive">Inactive</Select.Option>
              </Select>
              <Select
                style={{ minWidth: 150, maxWidth: 200 }}
                placeholder="Status"
                allowClear
                onChange={handleStatusFilterChange}
                value={statusFilter}
              >
                <Select.Option value="Pending">Pending</Select.Option>
                <Select.Option value="Reviewed">Reviewed</Select.Option>
                <Select.Option value="Shortlisted">Shortlisted</Select.Option>
                <Select.Option value="Rejected">Rejected</Select.Option>
              </Select>
              <Select
                style={{ minWidth: 150, maxWidth: 200 }}
                placeholder="Deleted Status"
                allowClear
                onChange={handleDeletedFilterChange}
                value={deletedFilter}
              >
                <Select.Option value="">All</Select.Option>
                <Select.Option value="Deleted">Deleted</Select.Option>
                <Select.Option value="Not Deleted">Not Deleted</Select.Option>
              </Select>
              <Input.Search
                ref={searchInputRef}
                placeholder="Search jobs..."
                allowClear
                enterButton={isSearching ? <LoadingOutlined /> : <SearchOutlined />}
                style={{ minWidth: 150, maxWidth: 300 }}
                value={searchText}
                onChange={(e) => handleSearch(e.target.value, e)}
                onSearch={(value) => handleSearch(value, { type: 'search' })}
                onClear={handleClearSearch}
                loading={isSearching}
              />
            </Space>
          </Col>
        </Row>
      </div>

      <div style={{ width: '100%', maxWidth: '1200px' }}>
        {applications.length === 0 && !loading ? (
          <Empty
            description={
              searchText || statusFilter || jobStatusFilter || deletedFilter
                ? `No applications found for "${searchText || ''}" ${statusFilter ? `with status ${statusFilter}` : ''} ${jobStatusFilter ? `for ${jobStatusFilter} jobs` : ''} ${deletedFilter ? `and deleted status ${deletedFilter}` : ''}`
                : 'No applications available'
            }
            style={{ margin: '40px 0' }}
          />
        ) : (
          <Table
            columns={columns}
            dataSource={applications}
            loading={loading}
            rowKey="id"
            bordered
            scroll={{
              x: 'max-content',
              y: 'calc(100vh - 340px)'
            }}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: total,
              onChange: handlePageChange,
              onShowSizeChange: handlePageChange,
              showSizeChanger: true,
              pageSizeOptions: ['5', '10', '15', '20'],
              showTotal: (total, range) => (
                <span>
                  Showing {range[0]}-{range[1]} of {total} records
                </span>
              ),
              position: ['bottomCenter'],
            }}
            style={{ width: '100%' }}
          />
        )}
      </div>

      <Modal
        title="Update Application Status"
        open={modalVisible}
        onCancel={resetModal}
        onOk={handleSubmit}
        width={400}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="status"
            label="Status"
            rules={[{ required: true, message: 'Please select a status!' }]}
          >
            <Select>
              <Select.Option value="Pending">Pending</Select.Option>
              <Select.Option value="Reviewed">Reviewed</Select.Option>
              <Select.Option value="Shortlisted">Shortlisted</Select.Option>
              <Select.Option value="Rejected">Rejected</Select.Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default JobsManagement;

